import { getUTMParams } from '@eneco/common/querystring';
import { clearStoredUTMParams, getStoredUTMParams } from '@eneco/common/querystring/utmStorage';
import type { RequestModels_Forms_B2BFormRequest, RequestModels_Forms_V2_FormsRequestModelV2 } from '@monorepo-types/dc';

import type { FormValues } from './types';

/**
 * Ported from `libs/sitecore/components/forms/src/GenericForm/util/request.ts` — that lib is
 * `lib:sitecore-components`, outside what `lib:cms-components` may depend on. The logic is already
 * CMS-agnostic (it operates on `FormValues`, not on JSS fields), so this is a copy, not a rewrite.
 * Both should go when the Sitecore lane does; until then a change to one needs the same change here.
 */

// Not every generic-form value is part of the Forms V2 request model in DC. Unsupported ones travel
// in `formFields` instead, and are consciously omitted from DATA_KEYS.
const DATA_KEYS = new Set([
  'emailAddress',
  'houseNumber',
  'houseNumberSuffix',
  'initials',
  'postalCode',
  'street',
  'surname',
  'surnamePreposition',
]);

const UNIQUE_KEY_IDENTIFIER_REGEX = /^unique_key_\d{3}$/;

/** Strips the `;unique_key_NNN` suffix the CRM appends to distinguish repeated field names. */
export const getFormFieldKey = (key: string): string => {
  const parts = key.split(';');
  if (parts.length > 1 && UNIQUE_KEY_IDENTIFIER_REGEX.test(parts.at(-1)!)) {
    parts.pop();
    return parts.join(';');
  }
  return key;
};

export const prepareRequest = (senderId: string, input: FormValues): RequestModels_Forms_V2_FormsRequestModelV2 => {
  const data: Record<string, unknown> = {};
  const formFields: { key: string; value: string }[] = [];

  for (const key in input) {
    if (key === 'recaptchaToken') continue;
    const value = input[key];

    if (DATA_KEYS.has(key)) {
      data[key] = value;
    } else {
      formFields.push({ key, value: `${Array.isArray(value) ? value.join(',') : value}` });
    }
  }

  return {
    data: {
      houseNumber: 0, // DC types wrongly mark houseNumber as non-optional.
      ...data,
      formFields,
      destinationCode: senderId,
      urlOrigin: window.location.href,
    },
  };
};

/** Last-touch attribution: current-URL UTMs win, stored ones are the fallback. */
export const utmFields = (): { key: string; value: string }[] => {
  const current = getUTMParams();
  const params = current && Object.keys(current).length > 0 ? current : (getStoredUTMParams() ?? {});
  clearStoredUTMParams();
  return Object.entries(params).map(([key, value]) => ({ key, value: value || '' }));
};

// The CRM's own names for the UTM parameters, and for the page the form was submitted from.
const CRM_UTM_KEYS: Record<string, string> = {
  utm_source: 'enc_utmsource',
  utm_medium: 'enc_utmmedium',
  utm_campaign: 'enc_utmcampaign',
  utm_content: 'enc_utmcontent',
  utm_term: 'enc_utmterm',
  utm_url: 'enc_utmwebpageurl',
};

/**
 * The Revolution CRM (B2B form) payload, ported like the rest of this file from
 * `libs/sitecore/components/forms/src/GenericForm/util/request.ts#prepareRevolutionFormRequest`.
 * Unlike `utmFields`, it sends the UTM parameters under the CRM's own names.
 */
export const prepareRevolutionFormRequest = (senderId: string, input: FormValues): RequestModels_Forms_B2BFormRequest => {
  const fields: { key: string; value: string }[] = [{ key: CRM_UTM_KEYS.utm_url, value: window.location.href }];

  for (const key in input) {
    if (key === 'recaptchaToken') continue;
    fields.push({ key: getFormFieldKey(key), value: `${input[key]}` });
  }

  const current = getUTMParams();
  const params = current && Object.keys(current).length > 0 ? current : (getStoredUTMParams() ?? {});
  fields.push(...Object.entries(params).map(([key, value]) => ({ key: CRM_UTM_KEYS[key] || key, value: value || '' })));
  clearStoredUTMParams();

  return { data: { fields, formId: senderId, publishedFormUrl: window.location.href } };
};
